import type {
  Diagnostic,
  DomainObject,
  ModelDocument,
  Relationship,
} from "../shared/contracts.js";
import { quoteIdentifier } from "./ddl.js";
type Binding = {
  tableName?: string;
  columnName?: string;
  schema?: string;
  alias?: string;
  executionContextId?: string;
  commitId?: string;
};
const binding = (o: DomainObject) =>
  (o.extensions["dataglow.source"] ?? {}) as Binding;
export function generateSemanticSQL(document: ModelDocument): {
  sql: string;
  diagnostics: Diagnostic[];
} {
  const diagnostics: Diagnostic[] = [];
  const fail = (code: string, message: string, objectId?: string) =>
    diagnostics.push({
      code,
      severity: "error",
      message,
      objectId,
      helpTopic: "semantic-grain",
    });
  if (document.model.family !== "semantic")
    return {
      sql: "",
      diagnostics: [
        {
          code: "SEMANTIC_FAMILY_REQUIRED",
          severity: "error",
          message: "Select a semantic model.",
        },
      ],
    };
  const entities = document.objects.filter(
      (o) =>
        o.kind === "semanticEntity" &&
        o.representationId === document.model.activeRepresentation,
    ),
    objects = new Map(document.objects.map((o) => [o.objectId, o]));
  const query = (document.extensions.semanticQuery ?? {}) as {
    rootObjectId?: string;
    fieldIds?: string[];
    measureIds?: string[];
    executionContextId?: string;
  };
  if (
    query.rootObjectId &&
    !entities.some((o) => o.objectId === query.rootObjectId)
  )
    fail(
      "SEMANTIC_ROOT_INVALID",
      "The selected root is not an active semantic entity.",
      query.rootObjectId,
    );
  const root =
    entities.find((o) => o.objectId === query.rootObjectId) ?? entities[0];
  if (!root)
    return {
      sql: "",
      diagnostics: [
        {
          code: "SEMANTIC_ENTITY_REQUIRED",
          severity: "error",
          message:
            "Create a semantic entity with an explicit grain and source binding.",
        },
      ],
    };
  const fields = document.objects.filter(
    (o) =>
      o.kind === "semanticField" &&
      entities.some((e) => e.objectId === o.ownerObjectId),
  );
  const measures = document.objects.filter(
    (o) =>
      o.kind === "measure" &&
      entities.some((e) => e.objectId === o.ownerObjectId),
  );
  const selected = query.fieldIds
    ? query.fieldIds
        .map((id) => objects.get(id))
        .filter((o): o is DomainObject => !!o)
    : fields;
  const selectedMeasures = query.measureIds
    ? query.measureIds
        .map((id) => objects.get(id))
        .filter((o): o is DomainObject => !!o)
    : measures;
  for (const id of [...(query.fieldIds ?? []), ...(query.measureIds ?? [])])
    if (!objects.has(id))
      fail(
        "SEMANTIC_UNKNOWN_SELECTION",
        "Selected field or metric does not exist.",
        id,
      );
  if (
    selected.some((f) => !fields.includes(f)) ||
    selectedMeasures.some((m) => !measures.includes(m))
  )
    fail(
      "SEMANTIC_SELECTION_KIND",
      "Output selection must use semantic fields and measures.",
    );
  const usedIds = new Set([
    root.objectId,
    ...selected.map((f) => f.ownerObjectId!),
    ...selectedMeasures.map((m) => m.ownerObjectId!),
  ]);
  for (const m of selectedMeasures) {
    const input = objects.get(m.sourceRef?.fieldId ?? m.expression ?? "");
    if (input?.ownerObjectId) usedIds.add(input.ownerObjectId);
  }
  const d = document.model.targetProfile ?? "postgresql",
    q = (name: string) => quoteIdentifier(name, d),
    aliases = new Map<string, string>();
  entities.forEach((e, i) =>
    aliases.set(e.objectId, binding(e).alias ?? `s${i + 1}`),
  );
  const context = query.executionContextId ?? binding(root).executionContextId;
  if (!context)
    fail(
      "SEMANTIC_DESIGN_ONLY",
      "Specify one reviewed execution context; cross-model design alone cannot execute.",
    );
  const checked = new Set<string>();
  const checkEntity = (e: DomainObject) => {
    if (checked.has(e.objectId)) return;
    checked.add(e.objectId);
    const b = binding(e);
    if (!b.tableName)
      fail(
        "SEMANTIC_SOURCE_UNBOUND",
        `Bind ${e.name} to a physical table.`,
        e.objectId,
      );
    if (!b.commitId && !e.sourceRef?.commitId)
      fail(
        "SEMANTIC_SOURCE_UNPINNED",
        "Source entity needs an immutable commit reference.",
        e.objectId,
      );
    if (b.executionContextId !== context)
      fail(
        "SEMANTIC_EXECUTION_CONTEXT",
        "All sources require the same explicit execution context; federation is unavailable.",
        e.objectId,
      );
    if (
      !e.grain?.length ||
      e.grain.some(
        (id) =>
          objects.get(id)?.ownerObjectId !== e.objectId ||
          objects.get(id)?.kind !== "semanticField",
      )
    )
      fail(
        "SEMANTIC_GRAIN_REQUIRED",
        "Declare grain using fields of the owning semantic entity.",
        e.objectId,
      );
  };
  const fieldSQL = (field: DomainObject): string => {
    const owner = objects.get(field.ownerObjectId ?? "");
    if (!owner || owner.kind !== "semanticEntity") {
      fail(
        "SEMANTIC_FIELD_OWNER",
        "Field owner is not a semantic entity.",
        field.objectId,
      );
      return "";
    }
    checkEntity(owner);
    const b = binding(field);
    if (!b.columnName) {
      fail(
        "SEMANTIC_COLUMN_UNBOUND",
        "Field needs an explicit physical column binding.",
        field.objectId,
      );
      return "";
    }
    if (!field.sourceRef?.commitId && !b.commitId)
      fail(
        "SEMANTIC_SOURCE_UNPINNED",
        "Field needs a source revision pin.",
        field.objectId,
      );
    const ownerPin = binding(owner).commitId ?? owner.sourceRef?.commitId,
      fieldPin = field.sourceRef?.commitId ?? b.commitId;
    if (ownerPin && fieldPin && ownerPin !== fieldPin)
      fail(
        "SEMANTIC_SOURCE_REVISION_MISMATCH",
        "Field and owning entity must bind the same immutable source revision.",
        field.objectId,
      );
    if (
      owner.sourceRef &&
      field.sourceRef &&
      (owner.sourceRef.modelId !== field.sourceRef.modelId ||
        owner.sourceRef.objectId !== field.sourceRef.objectId)
    )
      fail(
        "SEMANTIC_SOURCE_IDENTITY_MISMATCH",
        "Field source identity does not belong to its bound source entity.",
        field.objectId,
      );
    if (b.executionContextId && b.executionContextId !== context)
      fail(
        "SEMANTIC_EXECUTION_CONTEXT",
        "A field cannot override the reviewed source execution context.",
        field.objectId,
      );
    if (field.expression)
      fail(
        "SEMANTIC_EXPRESSION_UNSUPPORTED",
        "Arbitrary field expression text is not executable; a typed expression adapter is required.",
        field.objectId,
      );
    return `${q(aliases.get(owner.objectId)!)}.${q(b.columnName)}`;
  };
  // Join only a connected, unambiguous tree. Cyclic contexts and multiple candidate paths need explicit future context planning.
  const rels = document.relationships.filter(
    (r) =>
      r.representationId === document.model.activeRepresentation &&
      entities.some((e) => e.objectId === r.sourceObjectId) &&
      entities.some((e) => e.objectId === r.targetObjectId),
  );
  const visited = new Set([root.objectId]),
    candidateJoins: {
      rel: Relationship;
      next: DomainObject;
      reverse: boolean;
    }[] = [];
  let progress = true;
  while (progress) {
    progress = false;
    for (const rel of rels) {
      const source = visited.has(rel.sourceObjectId),
        target = visited.has(rel.targetObjectId);
      if (source === target) continue;
      const next = objects.get(
        source ? rel.targetObjectId : rel.sourceObjectId,
      )!;
      candidateJoins.push({ rel, next, reverse: !source });
      visited.add(next.objectId);
      progress = true;
    }
  }
  for (const id of usedIds)
    if (!visited.has(id))
      fail(
        "SEMANTIC_DISCONNECTED",
        "Requested field has no join path to the root.",
        id,
      );
  if (
    usedIds.size > 1 &&
    rels.filter(
      (r) => visited.has(r.sourceObjectId) && visited.has(r.targetObjectId),
    ).length !== candidateJoins.length
  )
    fail(
      "SEMANTIC_AMBIGUOUS_CONTEXT",
      "Join loops or alternate paths require an explicit supported context; no shortest-path guess is made.",
    );
  const parentJoin = new Map(candidateJoins.map((j) => [j.next.objectId, j]));
  const neededJoins = new Set<string>();
  for (const used of usedIds) {
    let cursor = used;
    const seen = new Set<string>();
    while (cursor !== root.objectId && !seen.has(cursor)) {
      seen.add(cursor);
      const j = parentJoin.get(cursor);
      if (!j) break;
      neededJoins.add(j.rel.relationshipId);
      cursor = j.reverse ? j.rel.targetObjectId : j.rel.sourceObjectId;
    }
  }
  const joins = candidateJoins.filter((j) =>
    neededJoins.has(j.rel.relationshipId),
  );
  const from = (e: DomainObject) => {
    const b = binding(e);
    checkEntity(e);
    return `${b.schema ? q(b.schema) + "." : ""}${q(b.tableName ?? "unbound")} AS ${q(aliases.get(e.objectId)!)}`;
  };
  const joinSQL: string[] = [];
  for (const { rel, next, reverse } of joins) {
    const nextCardinality = reverse
      ? rel.sourceCardinality
      : rel.targetCardinality;
    if (nextCardinality === "many") {
      fail(
        "SEMANTIC_FANOUT",
        "The join can multiply the root grain; preaggregation/allocation is required before compilation.",
        rel.relationshipId,
      );
      continue;
    }
    if (
      !rel.sourceAttributeIds?.length ||
      rel.sourceAttributeIds.length !== rel.targetAttributeIds?.length
    ) {
      fail(
        "SEMANTIC_JOIN_MEMBERS",
        "Join needs equal nonempty ordered field mappings.",
        rel.relationshipId,
      );
      continue;
    }
    const conditions: string[] = [];
    rel.sourceAttributeIds.forEach((id, i) => {
      const a = objects.get(id),
        b = objects.get(rel.targetAttributeIds![i]);
      if (
        !a ||
        !b ||
        a.ownerObjectId !== rel.sourceObjectId ||
        b.ownerObjectId !== rel.targetObjectId
      ) {
        fail(
          "SEMANTIC_JOIN_BINDING",
          "Join operands must belong to their declared endpoints.",
          rel.relationshipId,
        );
        return;
      }
      conditions.push(`${fieldSQL(a)} = ${fieldSQL(b)}`);
    });
    const joinType = rel.extensions?.joinType ?? "inner";
    if (
      !["inner", "left"].includes(String(joinType)) ||
      (reverse && joinType === "left")
    ) {
      fail(
        "SEMANTIC_JOIN_TYPE",
        "Only declared outward inner/left joins are supported; reversing outer joins is not inferred.",
        rel.relationshipId,
      );
      continue;
    }
    if (!rel.extensions?.cardinalityEvidence)
      diagnostics.push({
        code: "SEMANTIC_CARDINALITY_ASSUMED",
        severity: "warning",
        objectId: rel.relationshipId,
        message:
          "Join cardinality is authored intent; target uniqueness evidence has not been supplied.",
      });
    joinSQL.push(
      `${joinType === "left" ? "LEFT JOIN" : "JOIN"} ${from(next)} ON ${conditions.join(" AND ")}`,
    );
  }
  const projection = selected.map((f) => `${fieldSQL(f)} AS ${q(f.name)}`),
    grouping = selected.map(fieldSQL);
  for (const measure of selectedMeasures) {
    if (measure.ownerObjectId !== root.objectId)
      fail(
        "SEMANTIC_METRIC_OWNER_GRAIN",
        "Aggregating a joined entity metric at the root grain needs a reviewed preaggregation plan.",
        measure.objectId,
      );
    if (!measure.aggregation) {
      fail(
        "SEMANTIC_AGGREGATION_REQUIRED",
        "Choose an aggregation explicitly.",
        measure.objectId,
      );
      continue;
    }
    if (
      !["sum", "count", "distinct-count", "avg", "min", "max"].includes(
        measure.aggregation,
      )
    ) {
      fail(
        "SEMANTIC_AGGREGATION_UNSUPPORTED",
        "Unsupported aggregation; executable function text is not accepted.",
        measure.objectId,
      );
      continue;
    }
    if (
      measure.grain?.length &&
      measure.grain.some((id) => !root.grain?.includes(id))
    )
      fail(
        "SEMANTIC_METRIC_GRAIN",
        "Metric grain differs from the root and needs an explicit aggregation plan.",
        measure.objectId,
      );
    let input = "*";
    if (!(measure.aggregation === "count" && measure.expression === "*")) {
      const field = objects.get(
        measure.sourceRef?.fieldId ?? measure.expression ?? "",
      );
      if (!field || field.kind !== "semanticField") {
        fail(
          "SEMANTIC_MEASURE_INPUT",
          "Metric input must reference a semantic field ID; only COUNT accepts *.",
          measure.objectId,
        );
        continue;
      }
      input = fieldSQL(field);
    }
    const aggregation =
      measure.aggregation === "distinct-count"
        ? `COUNT(DISTINCT ${input})`
        : `${measure.aggregation.toUpperCase()}(${input})`;
    projection.push(`${aggregation} AS ${q(measure.name)}`);
  }
  if (
    new Set([...selected, ...selectedMeasures].map((o) => o.name)).size !==
    selected.length + selectedMeasures.length
  )
    fail(
      "SEMANTIC_OUTPUT_COLLISION",
      "Output fields and metrics require distinct aliases.",
    );
  if (!projection.length)
    fail("SEMANTIC_OUTPUT_REQUIRED", "Select at least one field or measure.");
  if (
    new Set([...checked, root.objectId].map((id) => aliases.get(id))).size !==
    new Set([...checked, root.objectId]).size
  )
    fail(
      "SEMANTIC_ALIAS_COLLISION",
      "Each source role needs a distinct alias.",
    );
  const source = from(root);
  diagnostics.push({
    code: "SEMANTIC_COMPILE_ONLY",
    severity: "info",
    message:
      "This is a compiled query artifact. Runtime authorization, bounded execution, policy enforcement and data evidence are separate.",
  });
  return {
    sql: diagnostics.some((d) => d.severity === "error")
      ? ""
      : `SELECT\n  ${projection.join(",\n  ")}\nFROM ${source}${joinSQL.length ? "\n" + joinSQL.join("\n") : ""}${selectedMeasures.length && grouping.length ? "\nGROUP BY " + grouping.join(", ") : ""};\n`,
    diagnostics,
  };
}
export function validateAnalytical(document: ModelDocument): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const add = (
    code: string,
    severity: Diagnostic["severity"],
    message: string,
    objectId?: string,
  ) =>
    diagnostics.push({
      code,
      severity,
      message,
      objectId,
      helpTopic: "analytical-grain",
    });
  const objects = new Map(document.objects.map((o) => [o.objectId, o]));
  if (document.model.family !== "analytical") {
    add("ANALYTICAL_FAMILY_REQUIRED", "error", "Select an analytical model.");
    return diagnostics;
  }
  for (const object of document.objects) {
    if (object.kind === "fact") {
      if (!object.grain?.length)
        add(
          "FACT_GRAIN_REQUIRED",
          "error",
          "A fact needs an explicit observation grain.",
          object.objectId,
        );
      else if (
        new Set(object.grain).size !== object.grain.length ||
        object.grain.some(
          (id) =>
            objects.get(id)?.ownerObjectId !== object.objectId ||
            !["column", "attribute", "semanticField"].includes(
              objects.get(id)?.kind ?? "",
            ),
        )
      )
        add(
          "FACT_GRAIN_INVALID",
          "error",
          "Fact grain must contain distinct fields belonging to the fact.",
          object.objectId,
        );
      if (!object.description?.trim())
        add(
          "FACT_MEANING_REQUIRED",
          "warning",
          "Describe what one fact row represents.",
          object.objectId,
        );
    }
    if (object.kind === "measure") {
      if (!object.aggregation)
        add(
          "MEASURE_AGGREGATION_REQUIRED",
          "error",
          "Choose aggregation; numeric datatype does not establish additivity.",
          object.objectId,
        );
      const policy = object.extensions.additivity as
        Record<string, unknown> | undefined;
      if (!policy)
        add(
          "MEASURE_ADDITIVITY_REQUIRED",
          "warning",
          "Declare additivity by dimension and time.",
          object.objectId,
        );
      if (policy?.time === "nonadditive" && object.aggregation === "sum")
        add(
          "MEASURE_NONADDITIVE_SUM",
          "error",
          "SUM over time is incompatible with nonadditive time policy.",
          object.objectId,
        );
      if (policy?.time === "semiadditive" && !object.extensions.timeAggregation)
        add(
          "MEASURE_TIME_RULE_REQUIRED",
          "warning",
          "Semiadditive time measures need an explicit last/first/as-of or other time rule.",
          object.objectId,
        );
    }
    if (object.kind === "dimension") {
      const scd = object.extensions.scd as
        | {
            kind?: string;
            businessKeyIds?: string[];
            effectiveFromId?: string;
            effectiveToId?: string;
            intervalConvention?: string;
          }
        | undefined;
      if (scd?.kind === "type2") {
        const ids = [
          ...(scd.businessKeyIds ?? []),
          scd.effectiveFromId,
          scd.effectiveToId,
        ];
        if (
          !scd.businessKeyIds?.length ||
          !scd.effectiveFromId ||
          !scd.effectiveToId ||
          ids.some(
            (id) => !id || objects.get(id)?.ownerObjectId !== object.objectId,
          )
        )
          add(
            "SCD_IDENTITY_INTERVAL_REQUIRED",
            "error",
            "Type2 dimensions need valid business-key and effective interval fields.",
            object.objectId,
          );
        if (scd.effectiveFromId === scd.effectiveToId)
          add(
            "SCD_INTERVAL_FIELDS",
            "error",
            "Start and end must be distinct fields.",
            object.objectId,
          );
        if (!scd.intervalConvention)
          add(
            "SCD_INTERVAL_CONVENTION",
            "warning",
            "Declare interval endpoints and historical fact lookup policy.",
            object.objectId,
          );
      }
    }
    if (object.kind === "hierarchy") {
      const parentMap = object.extensions.parentMap as
        Record<string, string> | undefined;
      if (parentMap)
        for (const start of Object.keys(parentMap)) {
          const seen = new Set<string>();
          let cursor: string | undefined = start;
          while (cursor) {
            if (seen.has(cursor)) {
              add(
                "HIERARCHY_CYCLE",
                "error",
                `Hierarchy cycle at ${cursor}.`,
                object.objectId,
              );
              break;
            }
            seen.add(cursor);
            cursor = parentMap[cursor];
          }
        }
    }
    const vault = object.extensions.dataVault as
      | {
          role?: string;
          businessKeyIds?: string[];
          parentIds?: string[];
          hashProfile?: {
            algorithm?: string;
            encoding?: string;
            nullEncoding?: string;
            memberOrder?: string[];
          };
        }
      | undefined;
    if (vault) {
      if (
        !["hub", "link", "satellite", "reference", "pit", "bridge"].includes(
          vault.role ?? "",
        )
      )
        add(
          "VAULT_ROLE_INVALID",
          "error",
          "Unknown Data Vault role.",
          object.objectId,
        );
      if (vault.role === "hub" && !vault.businessKeyIds?.length)
        add(
          "VAULT_BUSINESS_KEY_REQUIRED",
          "error",
          "A hub requires a business key, not only a hash column.",
          object.objectId,
        );
      if (
        ["link", "satellite"].includes(vault.role ?? "") &&
        !vault.parentIds?.length
      )
        add(
          "VAULT_PARENT_REQUIRED",
          "error",
          "Link/satellite parent roles must be explicit.",
          object.objectId,
        );
      if (
        vault.businessKeyIds?.some(
          (id) => objects.get(id)?.ownerObjectId !== object.objectId,
        )
      )
        add(
          "VAULT_BUSINESS_KEY_INVALID",
          "error",
          "Vault business-key fields must belong to the owning hub.",
          object.objectId,
        );
      if (vault.role === "link" && (vault.parentIds?.length ?? 0) < 2)
        add(
          "VAULT_LINK_PARENTS",
          "error",
          "A link requires at least two participating parent identities.",
          object.objectId,
        );
      if (vault.role === "satellite" && vault.parentIds?.length !== 1)
        add(
          "VAULT_SATELLITE_PARENT",
          "error",
          "A satellite requires one explicit owning parent.",
          object.objectId,
        );
      if (vault.parentIds?.some((id) => !objects.has(id)))
        add(
          "VAULT_PARENT_UNRESOLVED",
          "error",
          "Vault parent reference is unresolved.",
          object.objectId,
        );
      if (
        vault.hashProfile &&
        (!vault.hashProfile.algorithm ||
          !vault.hashProfile.encoding ||
          vault.hashProfile.nullEncoding === undefined ||
          !vault.hashProfile.memberOrder?.length)
      )
        add(
          "VAULT_HASH_PROFILE_INCOMPLETE",
          "error",
          "Hash profiles need algorithm, encoding, null representation and member order.",
          object.objectId,
        );
    }
  }
  if (
    !document.objects.some(
      (o) =>
        o.kind === "fact" || o.kind === "dimension" || o.extensions.dataVault,
    )
  )
    add(
      "ANALYTICAL_EMPTY",
      "warning",
      "Add fact/dimension or Data Vault roles to validate a design.",
    );
  return diagnostics;
}
